import { api } from '@/helpers/api'
import { UserActionInfo } from '@/helpers/api/user'
import { customerServiceLinks } from '@/helpers/commonConsts'
import { commonUtils } from '@/helpers/commonUtils'
import { envHelper } from '@/helpers/envHelper'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'

const WECHAT_SIGN_FULL_URL_STORAGE_KEY = 'wechat_sign_full_url'

class UserService {
  @observable userData: ServerData.UserData | null = null

  @observable
  courseMaterialRelation: DataModels.goods.CourseMaterialRelation[] = []

  @computed get isLogin() {
    return this.userData !== null
  }

  @computed get userId() {
    return this.userData?.inviteCode || this.userData?.userId
  }

  constructor() {
    makeObservable(this)
  }

  public async init() {
    await this.wxLoginCheck()
    this.updateUtmInfo()
    this.updateSalesCodeInfo()
    this.getUserInfo()
  }

  public async wxLoginCheck() {
    if (commonUtils.isWechat()) {
      const { code } = commonUtils.getUrlArgs()
      if (code && !this.userData?.userId) {
        try {
          sessionStorage.setItem(
            WECHAT_SIGN_FULL_URL_STORAGE_KEY,
            window.location.href.split('#')[0]
          )
        } catch (_error) {
          //
        }
        // 修复在当前页面授权登录后，刷新重新调用init()，但是当前页面参数地址的code为过期参数导致登录接口报错问题 即刷新也能可能会重复调用 
        // homesite_server/auth/code_login?code=011VM0Ia1b86NL0EKgJa1f4H612VM0I0&scope=snsapi_userinfo。接口问题
        await api.user.loginByWxGzh(code, envHelper.wxLoginScope)
        console.log('[wechat-login] before replaceState', window.location.href)
        commonUtils.replaceState({
          delete: ['code'],
        })
      }
    }
  }

  @action
  public async getUserInfo() {
    try {
      const data = await api.user.getUserInfo()
      this.userData = data.data
    } catch (error) {
      //
    }
  }

  public postUserActionRecord(data: UserActionInfo) {
    const isMobile = commonUtils.isMobile()
    const referer = commonUtils.getUrlArgs().referer || document.referrer
    const defaultRemark = {
      referer,
      utm: sessionStorage.getItem('utm') || undefined,
      slugCode: this.getSalesCode() || undefined,
    }

    let remark = data.remark
      ? {
          ...defaultRemark,
          ...data.remark,
        }
      : defaultRemark
    const _data = {
      ...data,
      clientType: !isMobile ? 'pc' : 'mobile',
      remark,
    }
    try {
      api.user.postUserActionRecord(_data)
    } catch (error) {}
  }

  public getLoginParams() {
    let { utm = '', kword = '', bd_vid: bdVid = '' } = commonUtils.getUrlArgs()
    if (!utm) {
      utm = localStorage.getItem('utm') || ''
    }
    if (!kword) {
      kword = localStorage.getItem('kword') || ''
    }
    let bdUrl = ''
    if (!bdVid) {
      bdVid = localStorage.getItem('bdVid') || ''
      bdUrl = localStorage.getItem('bdUrl') || ''
    } else {
      bdUrl = window.location.href
    }
    const baiduUrl = bdVid ? encodeURIComponent(bdUrl) : ''
    return { utm, kword: kword ? decodeFromGb2312(kword) : '', baiduUrl }
  }

  public removeUtmInfo() {
    localStorage.removeItem('utm')
    localStorage.removeItem('kword')
    localStorage.removeItem('bdVid')
    localStorage.removeItem('bdUrl')
  }

  public async logout() {
    try {
      await api.user.logout()
      runInAction(() => {
        this.userData = null
      })
    } catch (error) {}
  }

  // 获取所有学科分组；
  public async getCourseMaterialRelation() {
    if (this.courseMaterialRelation.length > 0) {
      return this.courseMaterialRelation
    }
    const { data } = await api.goods.getCourseMaterialRelation()
    runInAction(() => {
      this.courseMaterialRelation = data
    })
    return data
  }

  public updateUtmInfo() {
    const { utm, kword, bd_vid: bdVid } = commonUtils.getUrlArgs()
    if (utm) {
      localStorage.setItem('utm', utm)
      sessionStorage.setItem('utm', utm)
    }
    if (kword) {
      localStorage.setItem('kword', decodeFromGb2312(kword))
      sessionStorage.setItem('kword', decodeFromGb2312(kword))
    }
    if (bdVid) {
      localStorage.setItem('bdVid', bdVid)
      sessionStorage.setItem('bdVid', bdVid)
      localStorage.setItem('bdUrl', window.location.href)
      sessionStorage.setItem('bdUrl', window.location.href)
    }
  }

  public updateSalesCodeInfo() {
    const { slug_code: slugCode = '' } = commonUtils.getUrlArgs()
    if (!slugCode) {
      return
    }
    // localStorage.setItem('slug_code', slugCode)
    sessionStorage.setItem('slug_code', slugCode)
  }

  public getSalesCode() {
    return commonUtils.getCurrentSalesCode()
  }

  public getUserSource() {
    const { utm, kword, baiduUrl } = this.getLoginParams()
    if (utm || kword || baiduUrl) {
      return '广告'
    }
    return '自然流量'
  }

  public getContactQrUrl(from: 'pc' | 'mobile') {
    const { utm, kword, baiduUrl } = this.getLoginParams()
    const hasAd = !!(utm || kword || baiduUrl)
    if (from === 'pc') {
      if (hasAd) {
        switch (utm) {
          default:
            return commonUtils.getStaticFile('images/customer_qr/pc/ad.png')
        }
      } else {
        return commonUtils.getStaticFile('images/customer_qr/pc/home.png')
      }
    } else {
      if (hasAd) {
        switch (utm) {
          case 'zsjy':
            return commonUtils.getStaticFile('images/customer_qr/mobile/ad-zsjy.png')
          default:
            return commonUtils.getStaticFile('images/customer_qr/mobile/ad.png')
        }
      } else {
        return commonUtils.getStaticFile('images/customer_qr/mobile/home.png')
      }
    }
  }

  public openCustomerService() {
    if (this.getUserSource() === '广告') {
      window.open(customerServiceLinks.ad, '_blank')
    } else {
      window.open(customerServiceLinks.default, '_blank')
    }
  }
}

export const userService = new UserService()
